/* eslint-disable no-undef */
import React, { useState, useEffect, useMemo } from 'react';

// Import custom hooks
import { useFirebase } from './hooks/useFirebase';
import { useTheme } from './hooks/useTheme';
import { useBlogPosts } from './hooks/useBlogPosts';
import { useEditor } from './hooks/useEditor';
import { useDiaryEditor } from './hooks/useDiaryEditor';

// Import components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import MetaTags from './components/MetaTags';

// Import section components
import HomeSection from './components/sections/HomeSection';
import BlogSection from './components/sections/BlogSection';
import PostSection from './components/sections/PostSection';
import QuotesSection from './components/sections/QuotesSection';
import PoemsSection from './components/sections/PoemsSection';
import AdminSection from './components/sections/AdminSection';
import DiarySection from './components/sections/DiarySection';
import DiaryEntrySection from './components/sections/DiaryEntrySection';
import BookTimelineSection from './components/sections/BookTimelineSection';
import BankIdeasSection from './components/sections/BankIdeasSection';
import BankIdeaView from './components/BankIdeaView';
import SingleQuote from './components/SingleQuote';
import SinglePoem from './components/SinglePoem';
import { doc, updateDoc, increment } from 'firebase/firestore';

const App = () => {
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('p')) return 'post';
    if (params.has('quote')) return 'single-quote';
    if (params.has('poem')) return 'single-poem';
    if (params.has('tab')) return params.get('tab');
    return 'home';
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedQuoteId, setSelectedQuoteId] = useState(() => new URLSearchParams(window.location.search).get('quote'));
  const [selectedPoemId, setSelectedPoemId] = useState(() => new URLSearchParams(window.location.search).get('poem'));
  const [selectedDiaryEntry, setSelectedDiaryEntry] = useState(() => new URLSearchParams(window.location.search).get('d') ? {} : null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [adminTab, setAdminTab] = useState('hub');
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteText, setQuoteText] = useState('');
  const [quoteAuthor, setQuoteAuthor] = useState('');
  const [quoteDate, setQuoteDate] = useState('');
  const [quoteTags, setQuoteTags] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [selectedYear, setSelectedYear] = useState('All');
  
  // Reader Tools State - FULL RESTORATION
  const [textSizeMultiplier, setTextSizeMultiplier] = useState(1);
  const [likedPosts, setLikedPosts] = useState(() => {
    try {
      const saved = localStorage.getItem('likedPosts');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [copiedLink, setCopiedLink] = useState(false);
  const [likedDiaryEntries, setLikedDiaryEntries] = useState(() => {
    try {
      const saved = localStorage.getItem('likedDiaryEntries');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const { 
    user, isAdminLoggedIn, googleAccessToken, setGoogleAccessToken, posts, setPosts, quotes, setQuotes, poems, setPoems,
    diary, setDiary, ideas, saveIdea, deleteIdea,
    isLoading, loginError, handleGoogleLogin, handleLogout,
    requestDelete, submitQuote, savePost, updateQuote, categories, saveCategories, bulkDelete, db, appId,
    submitPoem, updatePoem, submitDiary, updateDiary
  } = useFirebase();

  const { isDarkMode, theme, toggleDarkMode } = useTheme();

  const publishedPosts = useMemo(() => 
    posts
      .filter(p => p.status === 'published')
      .filter(p => selectedYear === 'All' || (p.date && p.date.includes(selectedYear)))
      .sort((a, b) => {
        const dateA = new Date(a.date || 0);
        const dateB = new Date(b.date || 0);
        return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
      })
  , [posts, sortOrder, selectedYear]);

  const {
    selectedFilter, setSelectedFilter, searchQuery, setSearchQuery,
    currentPage, setCurrentPage, resetPagination, totalPages, currentBlogPosts
  } = useBlogPosts(publishedPosts, isAdminLoggedIn);

  const {
    isComposing, editingPostId, composeTitle, setComposeTitle,
    composeCategory, setComposeCategory, composeDate, setComposeDate,
    executeCommand, openNewPostEditor, openEditPostEditor,
    closeEditor, handleSavePost, triggerSmartSync, liveStats,
    saveStatus, editor,
    localDraftAvailable, restoreLocalDraft, clearLocalDraft,
    grammarIssues, isCheckingGrammar,
    handleGrammarCheck, replaceGrammarFix, clearGrammarIssues,
    ignoreGrammarIssue
  } = useEditor(savePost, user, categories);

  const diaryEditor = useDiaryEditor(submitDiary, updateDiary, user, categories);

  const adminStats = useMemo(() => ({
    published: posts.filter(p => p.status !== 'draft').length,
    drafts: posts.filter(p => p.status === 'draft').length,
    archives: quotes.length,
    diary: diary.length
  }), [posts, quotes, diary]);

  const handleNavigate = (tab, post = null, quoteId = null, poemId = null) => {
    let finalQuoteId = quoteId;
    let finalPoemId = poemId;
    let finalDiaryEntry = null;
    
    // Graceful parameter signature fallback: if tab is single-poem but poemId is empty, map quoteId
    if (tab === 'single-poem' && quoteId && !poemId) {
      finalPoemId = quoteId;
      finalQuoteId = null;
    }

    // Support diary-entry: second arg is the diary entry object
    if (tab === 'diary-entry' && post && !post.bookKey) {
      finalDiaryEntry = post;
    }

    // Support book-timeline: second arg has bookKey, bookTitle, bookAuthor
    if (tab === 'book-timeline' && post && post.bookKey) {
      setSelectedBook(post);
    }

    setActiveTab(tab);
    setSelectedPost(post && tab !== 'diary-entry' && tab !== 'book-timeline' ? post : null);
    setSelectedDiaryEntry(finalDiaryEntry);
    setSelectedQuoteId(finalQuoteId);
    setSelectedPoemId(finalPoemId);
    resetPagination();
    
    const params = new URLSearchParams();
    if (finalDiaryEntry) {
      params.set('d', finalDiaryEntry.id);
      const slug = (finalDiaryEntry.title || '').toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');
      if (slug) params.set('title', slug);
    }
    else if (tab === 'book-timeline' && post?.bookKey) {
      params.set('book', post.bookKey);
    }
    else if (post && tab !== 'diary-entry') {
      params.set('p', post.id);
      const slug = post.title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');
      params.set('title', slug);
    }
    else if (finalQuoteId) params.set('quote', finalQuoteId);
    else if (finalPoemId) params.set('poem', finalPoemId);
    else if (tab !== 'home') params.set('tab', tab);
    
    const queryStr = params.toString();
    const newUrl = queryStr ? `?${queryStr}` : window.location.pathname;
    window.history.pushState({ tab, postId: post?.id, quoteId: finalQuoteId, poemId: finalPoemId, diaryId: finalDiaryEntry?.id, bookKey: tab === 'book-timeline' ? post?.bookKey : null }, '', newUrl);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      const params = new URLSearchParams(window.location.search);
      const postId = params.get('p');
      const quoteId = params.get('quote');
      const poemId = params.get('poem');
      const diaryId = params.get('d');
      const bookKey = params.get('book');
      const tab = params.get('tab');
      if (postId) {
        if (posts.length > 0) {
          const post = posts.find(p => p.id === postId && (p.status !== 'draft' || isAdminLoggedIn));
          if (post) { setActiveTab('post'); setSelectedPost(post); }
          else { setActiveTab('home'); }
          setIsInitialLoad(false);
        }
      } else if (diaryId) {
        if (diary.length > 0) {
          const entry = diary.find(d => d.id === diaryId);
          if (entry) { setActiveTab('diary-entry'); setSelectedDiaryEntry(entry); }
          else { setActiveTab('home'); }
          setIsInitialLoad(false);
        }
      } else if (bookKey) {
        if (diary.length > 0) {
          const matchedEntry = diary.find(d => {
            const key = (d.title || '').toLowerCase().trim() + '|||' + (d.author || '').toLowerCase().trim();
            return key === bookKey;
          });
          if (matchedEntry) {
            setActiveTab('book-timeline');
            setSelectedBook({ bookKey, bookTitle: matchedEntry.title, bookAuthor: matchedEntry.author });
          } else { setActiveTab('home'); }
          setIsInitialLoad(false);
        }
      } else if (quoteId) {
        setActiveTab('single-quote');
        setSelectedQuoteId(quoteId);
        setIsInitialLoad(false);
      } else if (poemId) {
        setActiveTab('single-poem');
        setSelectedPoemId(poemId);
        setIsInitialLoad(false);
      } else {
        if (tab) { setActiveTab(tab); }
        setIsInitialLoad(false);
      }
    }
  }, [isLoading, isInitialLoad, posts, diary, isAdminLoggedIn]);

  // Reader Handlers
  const toggleLike = async (id) => {
    const isLiked = likedPosts[id];
    const newLikedState = { ...likedPosts, [id]: !isLiked };
    
    setLikedPosts(newLikedState);
    localStorage.setItem('likedPosts', JSON.stringify(newLikedState));

    if (db && appId) {
      try {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'posts', id), {
          likes: increment(isLiked ? -1 : 1)
        });
      } catch (error) {
        console.error("Failed to sync like:", error);
        // Rollback on failure
        const rollbackState = { ...likedPosts, [id]: isLiked };
        setLikedPosts(rollbackState);
        localStorage.setItem('likedPosts', JSON.stringify(rollbackState));
      }
    }
  };

  const toggleDiaryLike = async (id) => {
    const isLiked = likedDiaryEntries[id];
    const newLikedState = { ...likedDiaryEntries, [id]: !isLiked };

    setLikedDiaryEntries(newLikedState);
    localStorage.setItem('likedDiaryEntries', JSON.stringify(newLikedState));

    if (db && appId) {
      try {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'diary', id), {
          likes: increment(isLiked ? -1 : 1)
        });
      } catch (error) {
        console.error("Failed to sync diary like:", error);
        const rollbackState = { ...likedDiaryEntries, [id]: isLiked };
        setLikedDiaryEntries(rollbackState);
        localStorage.setItem('likedDiaryEntries', JSON.stringify(rollbackState));
      }
    }
  };

  const handleShareDiary = () => {
    let url = window.location.href;
    let clipboardText = url;

    if (selectedDiaryEntry) {
      url = `${window.location.origin}/?d=${selectedDiaryEntry.id}`;
      clipboardText = `*${selectedDiaryEntry.title}*\n\n${url}`;
    }

    if (navigator.share) {
      navigator.share({
        title: selectedDiaryEntry?.title || 'idiawisdom.com',
        text: 'Refleksi dari Diari Refleksi',
        url: url
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(clipboardText);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const handleShare = () => {
    let url = window.location.href;
    let shareTextAPI = 'Berfikir tentang apa sahaja yang terlintas laju.';
    let clipboardText = url;
    
    if (selectedPost) {
      const slug = selectedPost.title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');
      url = `${window.location.origin}/?p=${selectedPost.id}&title=${slug}`;
      shareTextAPI = `*${selectedPost.title}*`;
      clipboardText = `*${selectedPost.title}*\n\n${url}`;
    }
    
    if (navigator.share) {
      navigator.share({ 
        title: selectedPost?.title || 'idiawisdom.com', 
        text: shareTextAPI,
        url: url 
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(clipboardText);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const handleLogoutAndRedirect = async () => {
    await handleLogout();
    window.history.replaceState({}, '', window.location.origin);
    setActiveTab('home');
    setSelectedPost(null);
  };

  const currentUrl = selectedPost 
    ? `${window.location.origin}/?p=${selectedPost.id}&title=${selectedPost.title.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '')}`
    : window.location.href;

  return (
    <div className={`min-h-screen font-sans ${theme.bg} transition-colors duration-500`}>
      <MetaTags 
        title={selectedPost?.title} 
        description={selectedPost?.excerpt} 
        url={currentUrl}
        type={selectedPost ? 'article' : 'website'}
      />
      <Navbar 
        activeTab={activeTab} isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen}
        isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}
        isAdminLoggedIn={isAdminLoggedIn} handleNavigate={handleNavigate}
        handleLogout={handleLogoutAndRedirect} closeEditor={closeEditor} theme={theme}
      />
      
      <main className={`pt-24 pb-20 ${activeTab === 'admin' ? 'w-full' : 'max-w-4xl mx-auto px-6'}`}>
        {activeTab === 'home' && !selectedPost && <HomeSection handleNavigate={handleNavigate} theme={theme} isDarkMode={isDarkMode} />}
        {activeTab === 'blog' && !selectedPost && (
          <BlogSection 
            isLoading={isLoading} posts={posts} currentBlogPosts={currentBlogPosts}
            selectedFilter={selectedFilter} setSelectedFilter={setSelectedFilter}
            searchQuery={searchQuery} setSearchQuery={setSearchQuery}
            currentPage={currentPage} setCurrentPage={setCurrentPage}
            totalPages={totalPages} handleNavigate={handleNavigate} theme={theme} isDarkMode={isDarkMode}
            textSizeMultiplier={textSizeMultiplier} setTextSizeMultiplier={setTextSizeMultiplier}
            sortOrder={sortOrder} setSortOrder={setSortOrder}
            selectedYear={selectedYear} setSelectedYear={setSelectedYear}
          />
        )}

        {activeTab === 'post' && !selectedPost && isInitialLoad && (
          <div className="flex justify-center items-center py-40 animate-pulse">
            <span className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 ${theme.text}`}>Unlocking thoughts...</span>
          </div>
        )}
        
        {activeTab === 'post' && selectedPost && (
          <PostSection
            selectedPost={selectedPost}
            handleNavigate={handleNavigate}
            toggleLike={toggleLike} likedPosts={likedPosts}
            handleShare={handleShare} copiedLink={copiedLink}
            textSizeMultiplier={textSizeMultiplier}
            setTextSizeMultiplier={setTextSizeMultiplier}
            theme={theme} isDarkMode={isDarkMode}
          />
        )}
        
        {activeTab === 'single-quote' && selectedQuoteId && (
          <SingleQuote
            quoteId={selectedQuoteId}
            handleNavigate={handleNavigate}
            isDarkMode={isDarkMode}
            toggleDarkMode={toggleDarkMode}
            theme={theme}
          />
        )}

        {activeTab === 'single-poem' && selectedPoemId && (
          <SinglePoem
            poemId={selectedPoemId}
            handleNavigate={handleNavigate}
            isDarkMode={isDarkMode}
            toggleDarkMode={toggleDarkMode}
            theme={theme}
          />
        )}

        {activeTab === 'quotes' && <QuotesSection quotes={quotes} theme={theme} isDarkMode={isDarkMode} handleNavigate={handleNavigate} />}
        {activeTab === 'poems' && <PoemsSection poems={poems} theme={theme} isDarkMode={isDarkMode} handleNavigate={handleNavigate} />}
        {activeTab === 'diary' && <DiarySection diary={diary} theme={theme} isDarkMode={isDarkMode} isLoading={isLoading} handleNavigate={handleNavigate} />}

        {activeTab === 'bank-idea' && (
          <BankIdeasSection
            ideas={ideas}
            theme={theme}
            isDarkMode={isDarkMode}
            handleNavigate={handleNavigate}
          />
        )}

        {activeTab === 'bank-idea-view' && selectedPost && selectedPost.nodes && (
          <BankIdeaView
            idea={selectedPost}
            handleNavigate={handleNavigate}
            isDarkMode={isDarkMode}
            theme={theme}
          />
        )}

        {activeTab === 'book-timeline' && selectedBook && (
          <BookTimelineSection
            diary={diary}
            bookKey={selectedBook.bookKey}
            bookTitle={selectedBook.bookTitle}
            bookAuthor={selectedBook.bookAuthor}
            theme={theme} isDarkMode={isDarkMode}
            handleNavigate={handleNavigate}
          />
        )}

        {activeTab === 'diary-entry' && selectedDiaryEntry && (
          <DiaryEntrySection
            selectedDiaryEntry={selectedDiaryEntry}
            handleNavigate={handleNavigate}
            toggleDiaryLike={toggleDiaryLike} likedDiaryEntries={likedDiaryEntries}
            handleShareDiary={handleShareDiary} copiedLink={copiedLink}
            textSizeMultiplier={textSizeMultiplier}
            setTextSizeMultiplier={setTextSizeMultiplier}
            theme={theme} isDarkMode={isDarkMode}
            backToBook={selectedBook}
          />
        )}
        
        {activeTab === 'admin' && (
          <AdminSection
            isAdminLoggedIn={isAdminLoggedIn} isComposing={isComposing}
            googleAccessToken={googleAccessToken} setGoogleAccessToken={setGoogleAccessToken}
            isDarkMode={isDarkMode} theme={theme} posts={posts} setPosts={setPosts}
            quotes={quotes} setQuotes={setQuotes} poems={poems} setPoems={setPoems}
            diary={diary} setDiary={setDiary} submitDiary={submitDiary} updateDiary={updateDiary}
            ideas={ideas} saveIdea={saveIdea} deleteIdea={deleteIdea}
            diaryEditor={diaryEditor}
            submitPoem={submitPoem} updatePoem={updatePoem} loginError={loginError}
            handleGoogleLogin={handleGoogleLogin} handleLogout={handleLogoutAndRedirect}
            openNewPostEditor={openNewPostEditor} openEditPostEditor={openEditPostEditor}
            handleDeleteWithConfirm={requestDelete} adminTab={adminTab} setAdminTab={setAdminTab}
            showQuoteForm={showQuoteForm} setShowQuoteForm={setShowQuoteForm}
            quoteText={quoteText} setQuoteText={setQuoteText}
            quoteAuthor={quoteAuthor} setQuoteAuthor={setQuoteAuthor}
            quoteDate={quoteDate} setQuoteDate={setQuoteDate}
            quoteTags={quoteTags} setQuoteTags={setQuoteTags}
            submitQuote={submitQuote}
            adminStats={adminStats} updateQuote={updateQuote} 
            editingPostId={editingPostId} composeTitle={composeTitle} setComposeTitle={setComposeTitle}
            composeCategory={composeCategory} setComposeCategory={setComposeCategory}
            categories={categories || []} updateCategories={saveCategories}
            composeDate={composeDate} setComposeDate={setComposeDate}
            bulkDelete={bulkDelete}
            handleNavigate={handleNavigate}
            saveStatus={saveStatus} editor={editor} executeCommand={executeCommand}
            closeEditor={closeEditor} handleSavePost={handleSavePost}
            triggerSmartSync={triggerSmartSync} liveStats={liveStats}
            localDraftAvailable={localDraftAvailable}
            restoreLocalDraft={restoreLocalDraft}
            clearLocalDraft={clearLocalDraft}
            grammarIssues={grammarIssues}
            isCheckingGrammar={isCheckingGrammar}
            handleGrammarCheck={handleGrammarCheck}
            replaceGrammarFix={replaceGrammarFix}
            clearGrammarIssues={clearGrammarIssues}
            ignoreGrammarIssue={ignoreGrammarIssue}
          />
        )}
      </main>
      <Footer isDarkMode={isDarkMode} />
    </div>
  );
};

export default App;